// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Breadth.js
// Flexible breadth chart over the same sector / industry-group / commodity layers
// used by the announcement AI. Each line is the % of group members satisfying a
// selected technical condition on each trading day.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import api from '../api';
import Navbar from '../components/Navbar';

Chart.register(...registerables);

const RANGE_OPTIONS = ['3m', '6m', '1y', '2y', '5y'];
const SMOOTH_OPTIONS = [
  { v: 'off', l: 'Daily' },
  { v: '5', l: '5-session avg' },
  { v: '21', l: '21-session avg' },
];
const OP_OPTIONS = [
  { v: 'gt', l: '>' },
  { v: 'gte', l: '≥' },
  { v: 'lt', l: '<' },
  { v: 'lte', l: '≤' },
];

const keyOf = (kind, name) => `${kind}|${name}`;
const unpack = key => {
  const p = key.indexOf('|');
  return { kind: key.slice(0, p), name: key.slice(p + 1) };
};

function colourFor(kind, name) {
  let h = 0;
  const s = `${kind}:${name}`;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h} 66% 47%)`;
}

function rolling(points, win) {
  if (!win) return points;
  const out = [];
  const q = [];
  let sum = 0;
  for (const [x, y] of points) {
    q.push(y);
    sum += y;
    if (q.length > win) sum -= q.shift();
    out.push([x, sum / q.length]);
  }
  return out;
}

function metricLabel(spec, fields) {
  if (!spec) return '—';
  if (spec.key === 'value') return String(spec.value ?? '');
  if (spec.key === 'sma') return `SMA ${spec.period || 8}`;
  return fields.find(f => f.key === spec.key)?.label || spec.key;
}

function countFor(layer, exchange) {
  if (exchange === 'AU') return layer.au || 0;
  if (exchange === 'US') return layer.us || 0;
  return layer.n || 0;
}

const S = {
  page: { minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' },
  body: { maxWidth: '1500px', margin: '0 auto', padding: '22px 16px 48px' },
  hero: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '18px', flexWrap: 'wrap', marginBottom: '14px' },
  title: { fontSize: '22px', fontWeight: 680, margin: '0 0 5px', letterSpacing: '-0.01em' },
  sub: { fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.55, margin: 0, maxWidth: '850px' },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 1.5)', boxShadow: '0 1px 2px rgba(0,0,0,0.025)' },
  controls: { padding: '14px', marginBottom: '14px' },
  sectionLabel: { fontFamily: "'DM Mono',monospace", fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' },
  metaRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'end', paddingBottom: '13px', borderBottom: '1px solid var(--border)' },
  field: { display: 'flex', flexDirection: 'column', gap: '5px' },
  select: { minHeight: '34px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', borderRadius: 'var(--radius)', padding: '6px 9px', fontSize: '12px', fontFamily: "'DM Sans',sans-serif", outline: 'none' },
  input: { minHeight: '34px', width: '78px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', borderRadius: 'var(--radius)', padding: '6px 8px', fontSize: '12px', fontFamily: "'DM Mono',monospace", outline: 'none', boxSizing: 'border-box' },
  ruleArea: { paddingTop: '13px' },
  ruleRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'end' },
  metricWrap: { display: 'flex', gap: '5px', alignItems: 'center' },
  opSelect: { minHeight: '34px', width: '58px', border: '1px solid var(--accent)', background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 'var(--radius)', padding: '5px', fontSize: '16px', fontWeight: 700, textAlign: 'center' },
  summary: { marginTop: '11px', display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '6px 9px', borderRadius: '999px', background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: '12px', fontWeight: 600 },
  presetRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' },
  presetBtn: { border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-muted)', borderRadius: '999px', padding: '4px 9px', cursor: 'pointer', fontSize: '11px' },
  toolbar: { display: 'flex', flexWrap: 'wrap', gap: '7px', alignItems: 'center', marginBottom: '10px' },
  btn: active => ({ border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, background: active ? 'var(--accent)' : 'var(--surface)', color: active ? '#fff' : 'var(--text)', borderRadius: 'var(--radius)', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',sans-serif" }),
  chartCard: { padding: '12px', marginBottom: '14px' },
  chartHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' },
  chartTitle: { fontSize: '13px', fontWeight: 650 },
  chartMeta: { fontSize: '11px', color: 'var(--text-muted)' },
  chart: { height: '55vh', minHeight: '420px', position: 'relative' },
  empty: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '13px' },
  layers: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px' },
  panel: { padding: '14px' },
  panelHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' },
  panelTitle: { fontSize: '13px', fontWeight: 700 },
  panelSub: { fontSize: '10px', color: 'var(--text-muted)' },
  sectorCard: { borderTop: '1px solid var(--border)', padding: '10px 0' },
  sectorLine: { display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', fontWeight: 650 },
  groups: { display: 'flex', flexWrap: 'wrap', gap: '5px', margin: '7px 0 0 25px' },
  chip: (active, disabled = false) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, background: active ? 'var(--accent-dim)' : 'var(--bg)', color: active ? 'var(--accent)' : 'var(--text-muted)', borderRadius: '999px', padding: '4px 8px', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '11px', opacity: disabled ? 0.35 : 1 }),
  dot: color => ({ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }),
  commoditySection: { marginTop: '12px' },
  commodities: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '7px' },
  status: { fontSize: '11px', color: 'var(--text-muted)' },
  error: { color: 'var(--error)', fontSize: '12px', marginBottom: '10px', padding: '8px 10px', border: '1px solid color-mix(in srgb, var(--error) 35%, transparent)', borderRadius: 'var(--radius)' },
};

function MetricEditor({ value, onChange, fields, allowValue = false }) {
  const options = allowValue ? [...fields, { key: 'value', label: 'Value' }] : fields;
  const changeKey = key => {
    if (key === 'sma') onChange({ key, period: value?.period || 8 });
    else if (key === 'value') onChange({ key, value: value?.value ?? 50 });
    else onChange({ key });
  };

  return (
    <div style={S.metricWrap}>
      <select value={value?.key || 'close'} onChange={e => changeKey(e.target.value)} style={{ ...S.select, minWidth: '150px' }}>
        {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      {value?.key === 'sma' && (
        <input type="number" min="2" max="250" value={value.period || 8}
          onChange={e => onChange({ key: 'sma', period: Number(e.target.value) })}
          style={S.input} title="SMA period" />
      )}
      {value?.key === 'value' && (
        <input type="number" step="any" value={value.value ?? 50}
          onChange={e => onChange({ key: 'value', value: e.target.value })}
          style={{ ...S.input, width: '96px' }} title="Comparison value" />
      )}
    </div>
  );
}

export default function Breadth() {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [exchange, setExchange] = useState('AU');
  const [range, setRange] = useState('1y');
  const [smooth, setSmooth] = useState('21');
  const [condition, setCondition] = useState({
    left: { key: 'close' },
    op: 'gt',
    right: { key: 'sma', period: 20 },
    right_offset: 0,
  });
  const [series, setSeries] = useState([]);
  const [missing, setMissing] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingChart, setLoadingChart] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const requestRef = useRef(0);

  useEffect(() => {
    api.get('/breadth/layers')
      .then(r => {
        const c = r.data;
        setCatalog(c);
        const fields = c.metrics?.fields || [];
        const ema50 = fields.find(x => x.key === 'ema50' || x.key === 'ema050');
        if (ema50) {
          setCondition({ left: { key: 'close' }, op: 'gt', right: { key: ema50.key }, right_offset: 0 });
        }
        setSelected(new Set(
          (c.sectors || []).filter(s => (s.au || 0) > 0).map(s => keyOf('sector', s.name))
        ));
      })
      .catch(e => setError(e.response?.data?.error || 'Failed to load breadth setup'))
      .finally(() => setLoadingCatalog(false));
  }, []);

  const fields = catalog?.metrics?.fields || [];

  const allLayers = useMemo(() => {
    if (!catalog) return [];
    const rows = [];
    for (const s of catalog.sectors || []) {
      rows.push({ kind: 'sector', name: s.name, ...s });
      for (const g of s.groups || []) rows.push({ kind: 'group', name: g.name, ...g });
    }
    for (const c of catalog.commodities || []) rows.push({ kind: 'commodity', name: c.name, ...c });
    const seen = new Set();
    return rows.filter(x => {
      const k = keyOf(x.kind, x.name);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [catalog]);

  const availableKeys = useMemo(() => new Set(
    allLayers.filter(x => countFor(x, exchange) > 0).map(x => keyOf(x.kind, x.name))
  ), [allLayers, exchange]);

  useEffect(() => {
    if (!catalog) return;
    setSelected(prev => new Set([...prev].filter(k => availableKeys.has(k))));
  }, [catalog, availableKeys]);

  const presetLayers = kind => {
    let rows = [];
    if (kind === 'sector') rows = allLayers.filter(x => x.kind === 'sector');
    if (kind === 'group') rows = allLayers.filter(x => x.kind === 'group');
    if (kind === 'commodity') rows = allLayers.filter(x => x.kind === 'commodity');
    if (kind === 'metal') rows = allLayers.filter(x => x.kind === 'commodity' && x.metal);
    if (kind === 'all') rows = allLayers;
    setSelected(new Set(rows.map(x => keyOf(x.kind, x.name)).filter(k => availableKeys.has(k))));
  };

  const toggle = (kind, name) => {
    const k = keyOf(kind, name);
    if (!availableKeys.has(k)) return;
    setSelected(prev => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const metricPresets = useMemo(() => {
    const has = key => fields.some(f => f.key === key);
    const ema = n => fields.find(f => f.category === 'EMA' && Number((f.label.match(/\d+/) || [])[0]) === n)?.key;
    const out = [];
    const e20 = ema(20), e50 = ema(50), e200 = ema(200);
    if (e50) out.push({ label: 'Close > EMA 50', value: { left: { key: 'close' }, op: 'gt', right: { key: e50 }, right_offset: 0 } });
    if (e20 && e50) out.push({ label: 'EMA 20 > EMA 50', value: { left: { key: e20 }, op: 'gt', right: { key: e50 }, right_offset: 0 } });
    if (e50 && e200) out.push({ label: 'EMA 50 > EMA 200', value: { left: { key: e50 }, op: 'gt', right: { key: e200 }, right_offset: 0 } });
    if (has('bb_upper')) out.push({ label: 'Close > upper BB', value: { left: { key: 'close' }, op: 'gt', right: { key: 'bb_upper' }, right_offset: 0 } });
    if (has('bb_lower')) out.push({ label: 'Close < lower BB', value: { left: { key: 'close' }, op: 'lt', right: { key: 'bb_lower' }, right_offset: 0 } });
    out.push({ label: 'SMA 8 rising (5 sessions)', value: { left: { key: 'sma', period: 8 }, op: 'gt', right: { key: 'sma', period: 8 }, right_offset: 5 } });
    if (has('rsi14')) out.push({ label: 'RSI 14 > 50', value: { left: { key: 'rsi14' }, op: 'gt', right: { key: 'value', value: 50 }, right_offset: 0 } });
    return out;
  }, [fields]);

  useEffect(() => {
    if (!catalog || selected.size === 0) {
      setSeries([]);
      setMissing([]);
      setLoadingChart(false);
      return;
    }
    const seq = ++requestRef.current;
    const timer = setTimeout(() => {
      setLoadingChart(true);
      setError('');
      api.post('/breadth/metric', {
        exchange,
        range,
        series: [...selected].map(unpack),
        condition,
      })
        .then(r => {
          if (seq !== requestRef.current) return;
          setSeries(r.data.series || []);
          setMissing(r.data.missing || []);
        })
        .catch(e => {
          if (seq !== requestRef.current) return;
          setError(e.response?.data?.error || 'Failed to calculate breadth');
        })
        .finally(() => {
          if (seq === requestRef.current) setLoadingChart(false);
        });
    }, 180);
    return () => clearTimeout(timer);
  }, [catalog, selected, exchange, range, condition]);

  useEffect(() => {
    if (!canvasRef.current) return;
    chartRef.current?.destroy();
    if (!series.length) return;

    const win = smooth === 'off' ? 0 : Number(smooth);
    const datasets = series.map(s => {
      const color = colourFor(s.kind, s.name);
      const points = rolling(s.points, win);
      return {
        label: `${s.kind === 'group' ? 'Group' : s.kind === 'commodity' ? 'Commodity' : 'Sector'} · ${s.name}`,
        data: points.map(([x, y]) => ({ x, y })),
        borderColor: color,
        backgroundColor: 'transparent',
        borderWidth: 1.45,
        pointRadius: 0,
        pointHoverRadius: 3,
        tension: 0.08,
        spanGaps: true,
        _members: s.members,
      };
    });

    chartRef.current = new Chart(canvasRef.current.getContext('2d'), {
      type: 'line',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: c => `${c.dataset.label}: ${c.parsed.y.toFixed(1)}%`,
              afterLabel: c => c.dataset._members ? `${c.dataset._members} members with valid data` : '',
            },
          },
        },
        scales: {
          x: {
            type: 'time',
            grid: { color: 'rgba(128,128,128,0.12)' },
            ticks: { color: '#6B6B6B', maxTicksLimit: 10 },
          },
          y: {
            min: 0, max: 100,
            grid: { color: 'rgba(128,128,128,0.12)' },
            ticks: { color: '#6B6B6B', callback: v => `${v}%`, stepSize: 20 },
            title: { display: true, text: '% of members matching condition', color: '#6B6B6B' },
          },
        },
      },
    });
    return () => chartRef.current?.destroy();
  }, [series, smooth]);

  const summary = useMemo(() => {
    const op = OP_OPTIONS.find(o => o.v === condition.op)?.l || '>';
    const left = metricLabel(condition.left, fields);
    const right = metricLabel(condition.right, fields);
    const ago = condition.right?.key !== 'value' && condition.right_offset
      ? ` ${condition.right_offset} sessions ago`
      : '';
    return `% of members where ${left} ${op} ${right}${ago}`;
  }, [condition, fields]);

  if (loadingCatalog) {
    return <div style={S.page}><Navbar compact /><main style={S.body}><div style={S.empty}>Loading breadth engine…</div></main></div>;
  }

  const metals = (catalog?.commodities || []).filter(c => c.metal);
  const otherCommodities = (catalog?.commodities || []).filter(c => !c.metal);

  const commodityBlock = (title, rows) => (
    <div style={S.commoditySection}>
      <div style={S.sectionLabel}>{title}</div>
      <div style={S.commodities}>
        {rows.map(c => {
          const available = countFor(c, exchange) > 0;
          const active = selected.has(keyOf('commodity', c.name));
          return (
            <button key={c.name} disabled={!available} style={S.chip(active, !available)}
              onClick={() => toggle('commodity', c.name)}>
              <span style={S.dot(colourFor('commodity', c.name))} />
              {c.name}
              <span>{countFor(c, exchange).toLocaleString()}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <Navbar compact />
      <main style={S.body}>
        <div style={S.hero}>
          <div>
            <h1 style={S.title}>Market breadth</h1>
            <p style={S.sub}>
              Compare the percentage of companies inside any announcement-AI sector, industry group or commodity layer that satisfy the same technical condition each day.
            </p>
          </div>
          <div style={S.status}>{allLayers.length.toLocaleString()} available layer definitions</div>
        </div>

        <section style={{ ...S.card, ...S.controls }}>
          <div style={S.metaRow}>
            <div style={S.field}>
              <span style={S.sectionLabel}>Market</span>
              <select value={exchange} onChange={e => setExchange(e.target.value)} style={S.select}>
                <option value="AU">ASX</option>
                <option value="US">US</option>
                <option value="all">ASX + US</option>
              </select>
            </div>
            <div style={S.field}>
              <span style={S.sectionLabel}>Range</span>
              <div style={{ display: 'flex', gap: '5px' }}>
                {RANGE_OPTIONS.map(r => (
                  <button key={r} onClick={() => setRange(r)} style={S.btn(range === r)}>{r.toUpperCase()}</button>
                ))}
              </div>
            </div>
            <div style={S.field}>
              <span style={S.sectionLabel}>Display</span>
              <select value={smooth} onChange={e => setSmooth(e.target.value)} style={S.select}>
                {SMOOTH_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          </div>

          <div style={S.ruleArea}>
            <div style={S.sectionLabel}>Breadth condition</div>
            <div style={{ ...S.ruleRow, marginTop: '7px' }}>
              <div style={S.field}>
                <span style={S.sectionLabel}>Left metric</span>
                <MetricEditor value={condition.left} fields={fields}
                  onChange={left => setCondition(c => ({ ...c, left }))} />
              </div>
              <div style={S.field}>
                <span style={S.sectionLabel}>Operator</span>
                <select value={condition.op} onChange={e => setCondition(c => ({ ...c, op: e.target.value }))} style={S.opSelect}>
                  {OP_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div style={S.field}>
                <span style={S.sectionLabel}>Right metric / value</span>
                <MetricEditor value={condition.right} fields={fields} allowValue
                  onChange={right => setCondition(c => ({ ...c, right, right_offset: right.key === 'value' ? 0 : c.right_offset }))} />
              </div>
              {condition.right?.key !== 'value' && (
                <div style={S.field}>
                  <span style={S.sectionLabel}>Right side age</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="number" min="0" max="250" value={condition.right_offset || 0}
                      onChange={e => setCondition(c => ({ ...c, right_offset: Math.max(0, Math.min(250, Number(e.target.value) || 0)) }))}
                      style={S.input} />
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>sessions ago</span>
                  </div>
                </div>
              )}
            </div>

            <div style={S.summary}>{summary}</div>
            <div style={S.presetRow}>
              {metricPresets.map(p => (
                <button key={p.label} style={S.presetBtn} onClick={() => setCondition(p.value)}>{p.label}</button>
              ))}
            </div>
          </div>
        </section>

        <div style={S.toolbar}>
          <button style={S.btn(false)} onClick={() => presetLayers('sector')}>All sectors</button>
          <button style={S.btn(false)} onClick={() => presetLayers('group')}>All industry groups</button>
          <button style={S.btn(false)} onClick={() => presetLayers('commodity')}>All commodities</button>
          <button style={S.btn(false)} onClick={() => presetLayers('metal')}>Metals</button>
          <button style={S.btn(false)} onClick={() => presetLayers('all')}>Everything</button>
          <button style={S.btn(false)} onClick={() => setSelected(new Set())}>Clear</button>
          <span style={{ flex: 1 }} />
          <span style={S.status}>
            {selected.size.toLocaleString()} selected
            {series.length ? ` · ${series.length.toLocaleString()} plotted` : ''}
            {missing.length ? ` · ${missing.length.toLocaleString()} without usable data` : ''}
            {loadingChart ? ' · calculating…' : ''}
          </span>
        </div>

        {error && <div style={S.error}>{error}</div>}

        <section style={{ ...S.card, ...S.chartCard }}>
          <div style={S.chartHead}>
            <div>
              <div style={S.chartTitle}>{summary}</div>
              <div style={S.chartMeta}>{exchange === 'all' ? 'ASX + US combined' : exchange === 'AU' ? 'ASX equities' : 'US equities'} · {range.toUpperCase()} · {SMOOTH_OPTIONS.find(o => o.v === smooth)?.l}</div>
            </div>
            <div style={S.chartMeta}>0–100% breadth scale</div>
          </div>
          <div style={S.chart}>
            {selected.size === 0
              ? <div style={S.empty}>Choose one or more layers below, or use a quick-select button above.</div>
              : series.length === 0 && !loadingChart
                ? <div style={S.empty}>No usable data for this condition and selection.</div>
                : <canvas ref={canvasRef} />}
          </div>
        </section>

        {catalog && (
          <div style={S.layers}>
            <section style={{ ...S.card, ...S.panel }}>
              <div style={S.panelHead}>
                <div style={S.panelTitle}>Sectors & industry groups</div>
                <div style={S.panelSub}>Company counts for selected market</div>
              </div>
              {(catalog.sectors || []).map(s => {
                const available = countFor(s, exchange) > 0;
                return (
                  <div key={s.name} style={S.sectorCard}>
                    <label style={{ ...S.sectorLine, opacity: available ? 1 : 0.35 }}>
                      <input type="checkbox"
                        checked={selected.has(keyOf('sector', s.name))}
                        disabled={!available}
                        onChange={() => toggle('sector', s.name)} />
                      <span style={S.dot(colourFor('sector', s.name))} />
                      <span>{s.name}</span>
                      <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{countFor(s, exchange).toLocaleString()}</span>
                    </label>
                    {!!s.groups?.length && (
                      <div style={S.groups}>
                        {s.groups.map(g => {
                          const gAvailable = countFor(g, exchange) > 0;
                          const active = selected.has(keyOf('group', g.name));
                          return (
                            <button key={g.name} disabled={!gAvailable}
                              style={S.chip(active, !gAvailable)}
                              onClick={() => toggle('group', g.name)}>
                              <span style={S.dot(colourFor('group', g.name))} />
                              {g.name}
                              <span>{countFor(g, exchange).toLocaleString()}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>

            <section style={{ ...S.card, ...S.panel }}>
              <div style={S.panelHead}>
                <div style={S.panelTitle}>Commodities</div>
                <div style={S.panelSub}>Latest company tags</div>
              </div>
              {commodityBlock('Metals & minerals', metals)}
              {commodityBlock('Energy & other', otherCommodities)}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
